import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Lock, LogOut, FileText, ShoppingBag, FolderLock } from "lucide-react";
import Layout from "../components/Layout";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { customerApi } from "../lib/api";

export default function OsMeusCasos() {
  const { user, accessToken, loading, signOut, isSupabaseConfigured } = useCustomerAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (loading) return;
    if (!isSupabaseConfigured || !user) {
      nav("/entrar?redirect=/meus-casos");
      return;
    }
    customerApi
      .get("/me/orders", accessToken)
      .then((r) => setOrders(r.data.orders || []))
      .catch(async (e) => {
        // Token expired or revoked server-side → force a clean logout so the
        // user is taken to the login page instead of an inconsistent state.
        if (e?.response?.status === 401) {
          try { await signOut(); } catch (_) { /* noop */ }
          nav("/entrar?redirect=/meus-casos", { replace: true });
          return;
        }
        setErr(e.response?.data?.detail || "Erro ao carregar");
      })
      .finally(() => setBusy(false));
  }, [user, accessToken, loading, isSupabaseConfigured, nav, signOut]);

  if (loading || busy) {
    return (
      <Layout>
        <section className="py-32">
          <p className="mono text-sm text-bone-muted tracking-[0.3em] text-center">A CARREGAR DOSSIERS...</p>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="pt-16 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="section-tag mb-4">Área privada</p>
              <h1 className="headline text-5xl md:text-7xl">Os meus casos</h1>
              <p className="mono text-[11px] tracking-[0.2em] text-bone-muted mt-3">
                INVESTIGADOR: <span className="text-bone-dim">{user?.email}</span>
              </p>
            </div>
            <button
              onClick={async () => {
                try { await signOut(); } catch (_) { /* noop */ }
                nav("/", { replace: true });
              }}
              data-testid="logout-btn"
              className="btn-ghost"
            >
              <LogOut className="w-4 h-4" /> Sair
            </button>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {err && <p className="mono text-xs text-blood mb-6 tracking-[0.2em]">{err}</p>}

          {orders.length === 0 ? (
            <div className="dossier p-16 text-center">
              <FolderLock className="w-14 h-14 text-bone-faded mx-auto mb-6" />
              <h2 className="headline text-3xl mb-3">Ainda não tens dossiers</h2>
              <p className="text-bone-muted mb-8 max-w-lg mx-auto">
                Quando comprares um caso, aparece aqui com o link para descarregares o dossier confidencial em PDF.
              </p>
              <Link to="/loja" data-testid="empty-go-shop">
                <button className="btn-primary mx-auto">
                  Ver casos <ShoppingBag className="w-4 h-4" />
                </button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {orders.map((o) => (
                <div key={o.session_id} className="dossier group">
                  <div
                    className="aspect-[4/5] bg-cover bg-center relative"
                    style={{ backgroundImage: `url(${o.case_cover || ""})` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-100 via-ink-100/30 to-transparent" />
                    <span className="absolute top-4 left-4 mono text-[10px] tracking-[0.3em] bg-blood text-white px-3 py-1.5">
                      DESBLOQUEADO
                    </span>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="mono text-[10px] text-bone-faded mb-1 tracking-[0.3em]">{o.case_code}</p>
                      <h3 className="headline text-xl leading-tight">{o.case_title}</h3>
                    </div>
                  </div>
                  <div className="p-5 border-t border-white/10 space-y-3">
                    <div className="flex justify-between text-xs">
                      <span className="mono text-bone-faded tracking-[0.2em]">VALOR</span>
                      <span className="text-bone-dim">{o.amount?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="mono text-bone-faded tracking-[0.2em]">DATA</span>
                      <span className="text-bone-dim">{new Date(o.created_at).toLocaleDateString("pt-PT")}</span>
                    </div>
                    {o.case_pdf_url ? (
                      <a
                        href={o.case_pdf_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`download-${o.case_slug}`}
                        className="btn-primary w-full justify-center mt-4 text-[11px] inline-flex"
                      >
                        <Download className="w-4 h-4" /> Descarregar dossier
                      </a>
                    ) : (
                      <div className="border border-white/10 px-4 py-3 mt-4 flex items-center gap-2 text-bone-muted">
                        <Lock className="w-3.5 h-3.5" />
                        <span className="mono text-[10px] tracking-[0.2em]">PDF em preparação</span>
                      </div>
                    )}
                    <Link to={`/casos/${o.case_slug}`} className="block">
                      <div className="text-center mono text-[11px] text-bone-muted hover:text-white tracking-[0.2em] py-2 mt-1">
                        <FileText className="w-3 h-3 inline mr-2" /> Ver detalhes do caso
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
